import { useTranslation } from 'react-i18next';
import { LogOut, User, LogIn, Map } from 'lucide-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Small, Muted } from '../../ui/typography';
import { useAuthStore } from '@/store/auth';
import { paths } from '@/constants';

export function UserMenu() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { locale?: string };
  const locale = params.locale || i18n.resolvedLanguage || i18n.language || 'en';
  const user = useAuthStore.use.user();
  const logout = useAuthStore.use.logout();
  const loading = useAuthStore.use.loading();
  const [imageError, setImageError] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const navigateTo = (
    path: '/$locale/auth/authorize' | '/$locale/profile' | '/$locale/itineraries'
  ) => {
    navigate({ to: path, params: { locale } });
  };

  if (!user) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className='flex items-center space-x-2 text-(--color-text) hover:bg-white/20 focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0'
          >
            <div className='w-8 h-8 rounded-full bg-(--color-main) text-white flex items-center justify-center text-sm font-medium'>
              <User className='w-5 h-5' />
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='end'
          className='min-w-45 bg-white/70 backdrop-blur-md border-(--color-main)/30 shadow-lg'
        >
          <div className='px-2 py-1.5'>
            <Small className='font-medium text-foreground'>
              {t('auth.guestMenu.title')}
            </Small>
            <Muted className='text-xs'>
              {t('auth.guestMenu.signInToSave')}
            </Muted>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => navigateTo(paths.auth)}
            className='flex items-center space-x-2 hover:bg-(--color-main)/10 cursor-pointer'
          >
            <LogIn className='w-4 h-4 text-foreground' />
            <Small className='text-foreground'>{t('auth.signIn')}</Small>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const displayName = user.name || user.email?.split('@')[0] || 'User';
  const initials = displayName
    .split(' ')
    .map((name: string) => name[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className='flex items-center space-x-2 text-(--color-text) hover:bg-white/20 focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0'
          >
            <div className='w-8 h-8 rounded-full bg-(--color-main) text-white flex items-center justify-center text-sm font-medium'>
              {user.avatar_url && !imageError ? (
                <img
                  src={user.avatar_url}
                  alt={displayName}
                  className='w-full h-full rounded-full object-cover'
                  onError={handleImageError}
                />
              ) : (
                initials
              )}
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='end'
          className='min-w-45 bg-white/70 backdrop-blur-md border-(--color-main)/30 shadow-lg'
        >
          <div className='px-2 py-1.5'>
            <Small className='font-medium text-foreground'>{displayName}</Small>
            <Muted className='text-xs'>{user.email}</Muted>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => navigateTo(paths.profile)}
            className='flex items-center space-x-2 hover:bg-(--color-main)/10 cursor-pointer'
          >
            <User className='w-4 h-4 text-foreground' />
            <Small className='text-foreground'>{t('profile.menuTitle')}</Small>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => navigateTo(paths.itineraries)}
            className='flex items-center space-x-2 hover:bg-(--color-main)/10 cursor-pointer'
          >
            <Map className='w-4 h-4 text-foreground' />
            <Small className='text-foreground'>
              {t('itineraries.menuTitle')}
            </Small>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleLogout}
            disabled={loading}
            className='flex items-center space-x-2 hover:bg-(--color-main)/10 cursor-pointer'
          >
            <LogOut className='w-4 h-4 text-foreground' />
            <Small className='text-foreground'>
              {loading ? t('auth.signingOut') : t('auth.logout')}
            </Small>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
